<script lang="ts" setup>
import { segmentation } from '@thecompaniesapi/shared/client'

const { getCompanyAttributes, getMetaAttributes, getQueryAttributes, getSortAttributes } = useApiAttributes()
const { refreshResponseWithApiVersion, version } = useApiDoc()
const { t } = useI18n()
const api = useApi()
const localePath = useLocalePath()

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: '/companies',
  },
  {
    method: 'POST',
    path: '/companies',
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    attributes: getQueryAttributes(),
    description: t('attributes.query'),
    endpoint: '/companies',
    hasSegmentation: true,
    name: 'query',
    required: true,
    type: 'string',
    warning: t('globals.stringifyAndEncode'),
  },
  {
    description: t('attributes.domainsToExclude'),
    example: '/companies?domainsToExclude=amazon.com,pearson.com',
    name: 'domainsToExclude',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.linkedinToExclude'),
    example: '/companies?linkedinToExclude=https://www.linkedin.com/company/amazon,https://www.linkedin.com/company/pearson',
    name: 'linkedinToExclude',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.page'),
    example: '/companies?page=1',
    name: 'page',
    optional: true,
    type: 'number',
  },
  {
    description: t('attributes.search'),
    example: '/companies?search=amazon',
    name: 'search',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.searchFields'),
    example: '/companies?searchFields[]=domain&searchFields[]=name',
    name: 'searchFields',
    optional: true,
    type: 'array',
    values: segmentation.attributesWeCanSearch,
  },
  {
    description: t('attributes.simplified'),
    example: '/companies?simplified=true',
    name: 'simplified',
    optional: true,
    type: 'boolean',
    values: ['false', 'true'],
  },
  {
    description: t('attributes.size'),
    example: '/companies?size=10',
    name: 'size',
    optional: true,
    type: 'number',
  },
  ...getSortAttributes({ endpoint: '/companies' }),
]

const responseAttributes = computed((): ApiAttribute[] => {
  return [
    {
      attributes: getCompanyAttributes({ version: version.value }),
      description: t('response.companies'),
      link: {
        label: t('globals.browseAllDatapoints'),
        to: localePath({
          name: 'api-slug',
          params: { slug: 'datapoints' },
        }),
      },
      name: 'companies',
      type: 'array',
    },
    getMetaAttributes(),
    {
      attributes: getQueryAttributes(),
      description: t('response.query'),
      name: 'query',
      type: 'array',
    },
  ]
})

async function onVisibilityChange(visibility: boolean) {
  refreshResponseWithApiVersion({
    api,
    execute,
    response,
    visibility,
  })
}

const { data: response, execute, status } = useAsyncData(
  'doc:companies-search',
  () => api.companies.fetch(),
  { immediate: false },
)
</script>

<template>
  <ApiDoc
    :key="version"
    :endpoints="endpoints"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "domainsToExclude": "A list of comma-separated domains to exclude from the results.",
      "linkedinToExclude": "A list of comma-separated LinkedIn URLs to exclude from the results.",
      "page": "The page to fetch (default to 1).",
      "query": "An array of conditions the companies must match.",
      "search": "An additional search query applied to the company name or domain.",
      "searchFields": "Define the fields to search.",
      "simplified": "Return a simplified version of the companies (no credits deducted but limitation are applied on the free plan). It is useful for previewing the data before fetching the full version. Default to false.",
      "size": "The number of companies to be returned (between 1 and 100). Default to 10."
    },
    "response": {
      "companies": "The companies that match your conditions.",
      "query": "The conditions the companies must match."
    }
  },
  "es": {
    "attributes": {
      "domainsToExclude": "Una lista de dominios separados por comas para excluir de los resultados.",
      "linkedinToExclude": "Una lista de URLs de LinkedIn separadas por comas para excluir de los resultados.",
      "page": "La página a recuperar (por defecto 1).",
      "query": "Un array de condiciones que las empresas deben cumplir.",
      "search": "Una consulta de búsqueda adicional aplicada al nombre de la empresa o al dominio.",
      "searchFields": "Defina los campos para buscar.",
      "simplified": "Devuelve una versión simplificada de las empresas (no se deduce ningún crédito, pero se aplican limitaciones en el plan gratuito). Es útil para visualizar los datos antes de recuperar la versión completa. Por defecto a false.",
      "size": "El número de empresas a devolver (entre 1 y 100). Por defecto 10."
    },
    "response": {
      "companies": "Las empresas que coinciden con tus condiciones.",
      "query": "Las condiciones que las empresas deben cumplir."
    }
  },
  "fr": {
    "attributes": {
      "domainsToExclude": "Une liste de domaines séparés par des virgules à exclure des résultats.",
      "linkedinToExclude": "Une liste d'URLs LinkedIn séparées par des virgules à exclure des résultats.",
      "page": "La page à récupérer (par défaut 1).",
      "query": "Un array de conditions que les entreprises doivent remplir.",
      "search": "Une requête de recherche supplémentaire appliquée au nom de l'entreprise ou au domaine.",
      "searchFields": "Définissez les champs à rechercher.",
      "simplified": "Retourne une version simplifiée des entreprises (aucun crédit déduit mais des limitations sont appliquées sur le plan gratuit). C'est utile pour prévisualiser les données avant de récupérer la version complète. Par défaut à false.",
      "size": "Le nombre d'entreprises à retourner (entre 1 et 100). Par défaut 10."
    },
    "response": {
      "companies": "Les entreprises qui correspondent à vos conditions.",
      "query": "Les conditions que les entreprises doivent remplir."
    }
  }
}
</i18n>
